<template>
  <span>
    <span :id="badge_id" class="regime-badge" :class="verdict_class">
      {{ label }}
      <b-icon icon="info-circle" scale="0.85" class="regime-badge-icon"></b-icon>
    </span>
    <b-popover
      :target="badge_id"
      triggers="hover"
      placement="left"
      boundary="viewport"
      custom-class="regime-popover"
      @show="fetchExplanation"
    >
      <template #title>{{ $t("toolbar.reports.regime").toUpperCase() }}</template>
      <template>
        <div class="regime-panel">
          <div v-if="!explanation">{{ $t("reusable.common.loading") }}</div>
          <div v-else-if="explanation.error" class="regime-issue">
            {{ explanation.error }}
          </div>
          <template v-else>
            <div class="regime-verdict" :class="verdict_class">
              {{ verdictLabel(explanation.is_on_track) }}
            </div>
            <div class="regime-reason">{{ explanation.reason }}</div>
            <div class="regime-chips">
              <span class="regime-chip">
                {{ $t("toolbar.reports.cohort") }}:
                <strong>{{ explanation.calculated_cohort }}</strong>
              </span>
              <span class="regime-chip">
                {{ $t("toolbar.reports.declaredCohort") }}:
                <strong>{{ explanation.declared_cohort }}</strong>
              </span>
              <span class="regime-chip" v-if="explanation.expected_level">
                {{ $t("toolbar.reports.expectedLevel") }}:
                <strong>{{ explanation.expected_level }}</strong>
              </span>
            </div>
            <div
              v-for="(issue, issue_index) in explanation.issues"
              :key="`issue-${issue_index}`"
              class="regime-issue"
            >
              {{ issue }}
            </div>
            <div
              v-for="plan_level in explanation.levels"
              :key="`level-${plan_level.level}`"
              class="regime-level"
              :class="`stage-${plan_level.stage}`"
            >
              <div class="regime-level-title">
                {{ $t("toolbar.reports.level") }} {{ plan_level.level }}
                <span
                  v-if="plan_level.stage"
                  class="regime-stage-tag"
                  :class="`tag-${plan_level.stage}`"
                  >{{ stageLabel(plan_level.stage) }}</span
                >
              </div>
              <div
                v-for="(course, course_index) in plan_level.courses"
                :key="`course-${plan_level.level}-${course_index}`"
                class="regime-course"
              >
                <span
                  class="regime-grade"
                  :class="`grade-${course.state}`"
                  :title="courseStateLabel(course.state)"
                  >{{ course.grade }}</span
                >
                <span class="regime-course-name">{{ course.name }}</span>
                <span class="regime-course-period">{{ course.period }}</span>
              </div>
            </div>
          </template>
        </div>
      </template>
    </b-popover>
  </span>
</template>

<script>
// Contador de módulo (bloque normal, se ejecuta una sola vez, no por instancia):
// garantiza un id de DOM único por instancia del componente, incluso cuando el
// mismo estudiante se renderiza en paralelo (ver badge_id en <script setup>)
let regime_badge_instance_counter = 0;
</script>

<script setup>
// eslint-plugin-vue 6.x (anterior a <script setup>) no ve que estos bindings
// se usan en el template. Quitar el disable cuando se actualice el tooling.
/* eslint-disable no-unused-vars */
import { computed, getCurrentInstance, ref } from "vue";

const props = defineProps({
  evaluatee_id: {
    type: [Number, String],
    required: true,
  },
  // Tri-estado: true en régimen, false no en régimen, null indeterminado
  is_on_track: {
    default: null,
  },
  // Cada reporte conserva su propio rótulo (En Régimen / No en Régimen, o Sí / No)
  label: {
    type: String,
    default: "",
  },
});

const { proxy } = getCurrentInstance();
const explanation = ref(null);

// El mismo estudiante puede renderizarse en paralelo más de una vez (pestañas de
// Asignatura/RA Macro/RA de Unidad montadas a la vez con b-tabs, sin lazy). Un id
// basado solo en evaluatee_id se duplicaría en el DOM y b-popover (que resuelve su
// target con getElementById) apuntaría siempre al primer nodo encontrado, mezclando
// peticiones entre pestañas. El contador de instancia lo hace único por badge.
regime_badge_instance_counter += 1;
const instance_id = regime_badge_instance_counter;
const badge_id = computed(() => `regime-badge-${instance_id}-${props.evaluatee_id}`);

const verdict_class = computed(() => {
  if (props.is_on_track === true) return "verdict-on";
  if (props.is_on_track === false) return "verdict-off";
  return "verdict-unknown";
});

function verdictLabel(is_on_track) {
  if (is_on_track === true) return proxy.$t("toolbar.reports.inRegime");
  if (is_on_track === false) return proxy.$t("toolbar.reports.notInRegime");
  return "-";
}

function stageLabel(stage) {
  if (stage === "previous") return proxy.$t("toolbar.reports.stagePrevious");
  if (stage === "current") return proxy.$t("toolbar.reports.stageCurrent");
  if (stage === "future") return proxy.$t("toolbar.reports.stageFuture");
  return "";
}

function courseStateLabel(state) {
  if (state === "approved") return proxy.$t("toolbar.reports.gradeApproved");
  if (state === "failed") return proxy.$t("toolbar.reports.gradeFailed");
  return proxy.$t("toolbar.reports.gradeInProgress");
}

// Se pide al abrir el panel y queda cacheado, para no repetir la consulta por hover
function fetchExplanation() {
  if (explanation.value) return;
  proxy.$restful
    .Post(`/cideu/student_tracking_explanation/`, {
      student: props.evaluatee_id,
    })
    .then((response) => {
      explanation.value = response;
    })
    .catch(() => {
      explanation.value = {
        error: proxy.$t("reusable.common.problemGenerating"),
      };
    });
}
</script>

<style scoped>
/* Pastilla en la celda: color según estado e ícono que anticipa el detalle */
.regime-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 10px;
  border: 1px solid transparent;
  white-space: nowrap;
  cursor: help;
  transition: filter 0.15s ease, box-shadow 0.15s ease;
}
.regime-badge:hover {
  filter: brightness(0.95);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}
.regime-badge.verdict-on {
  background-color: #e6f4ea;
  border-color: #2e7d32;
  color: #1b5e20;
}
.regime-badge.verdict-off {
  background-color: #fdecea;
  border-color: #c62828;
  color: #b71c1c;
}
.regime-badge.verdict-unknown {
  background-color: #f5f5f5;
  border-color: #9e9e9e;
  color: #616161;
}
.regime-badge-icon {
  opacity: 0.65;
}
.regime-panel {
  max-height: 440px;
  overflow: auto;
  min-width: 340px;
  font-size: 9.5pt;
  text-align: left;
}
.regime-verdict {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-weight: bold;
  color: #fff;
}
.regime-verdict.verdict-on {
  background-color: #2e7d32;
}
.regime-verdict.verdict-off {
  background-color: #c62828;
}
.regime-verdict.verdict-unknown {
  background-color: #ef6c00;
}
.regime-reason {
  margin: 4px 0;
}
.regime-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
}
.regime-chip {
  background-color: #eceff1;
  border-radius: 8px;
  padding: 1px 6px;
  font-size: 8pt;
}
.regime-issue {
  background-color: #fdecea;
  border-left: 3px solid #c62828;
  padding: 2px 6px;
  margin-bottom: 3px;
}
.regime-level {
  border-left: 3px solid #cfd8dc;
  padding-left: 6px;
  margin-top: 5px;
}
.regime-level.stage-previous {
  border-left-color: #90a4ae;
}
.regime-level.stage-current {
  border-left-color: var(--kl-color-accent-medium, #1565c0);
}
.regime-level.stage-future {
  border-left-color: #e0e0e0;
}
.regime-level-title {
  font-weight: bold;
}
.regime-stage-tag {
  font-weight: normal;
  font-size: 7.5pt;
  border-radius: 8px;
  padding: 0 5px;
  margin-left: 4px;
  color: #fff;
}
.tag-previous {
  background-color: #90a4ae;
}
.tag-current {
  background-color: var(--kl-color-accent-medium, #1565c0);
}
.tag-future {
  background-color: #bdbdbd;
}
.regime-course {
  display: flex;
  align-items: baseline;
  gap: 5px;
}
.regime-grade {
  min-width: 26px;
  text-align: center;
  border-radius: 4px;
  font-weight: bold;
  color: #fff;
}
.grade-approved {
  background-color: #2e7d32;
}
.grade-failed {
  background-color: #c62828;
}
.grade-in_progress {
  background-color: #ef6c00;
}
.regime-course-name {
  flex: 1;
}
.regime-course-period {
  color: #78909c;
  font-size: 8pt;
}
</style>

<!-- Sin scoped: BootstrapVue monta el popover fuera del componente, así que el
     contenedor .popover no recibe el atributo de scope. La clase propia acota el
     alcance para no afectar al resto de los popovers. -->
<style>
.popover.regime-popover {
  max-width: 420px;
}
.popover.regime-popover .popover-body {
  padding: 8px 10px;
}
</style>
